/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { findStandardUse } from 'apis/ut/UtMatrixRegist';
import { UtMatrixDetail, UtMatrixRegistSearchCondition } from 'models/ut/UtMatrix';
import { hasRole } from 'utils/SessionUtil';
import { useLoading } from 'components/process/Loading';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';

/**
 * @desc 작성 > [표준사용량 비교] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.01   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리)
 * 2025.10.04   jshwang12       N2 AVG (사용량, 표준사용량, 수용율) 추가 (표준사용량은 N2 Peak와 동일한 값 사용)
 * 2026.08.05   jihwan89        CSR E20260727-000228 Vacuum Pump 추가
 */
const StandardUseContent = (props: any, ref) => {
  const {
    onSubmit,
    condition = {} as UtMatrixRegistSearchCondition,
    processData = [] as UtMatrixDetail[],
  } = props;
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { isLoading } = useLoading();
  const [rowData, setRowData] = useState<UtMatrixDetail[]>([]);
  const defaultGridHeight = 388;

  const isUtWriter = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return (p.length > 0 && p[0].utmWriteYn === 'Y') || hasRole('ADM');
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);
  const isWrite = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return p.length > 0 && p[0].utmWrtProcProgStatCd === 'UTP02';
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);
  const isWriteComplete = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return p.length > 0 && p[0].utmWrtProcProgStatCd === 'UTP03';
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);

  useImperativeHandle(ref, () => ({
    searchStandardUse: (condition) => {
      searchStandardUse(condition);
    },
  }));

  const searchStandardUse = (condition) => {
    findStandardUse(condition).then((result) => {
      setRowData(result);
    });
  };

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      align: 'center',
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'prdnProcCdNm',
      header: String(t('ut.label.공정', '공정')),
      width: 100,
      align: 'center',
    },
    {
      binding: 'qty',
      header: String(t('ut.label.라인/설비 수량', '라인/설비 수량')),
      width: 100,
      align: 'center',
      cellTemplate: (params) =>
        `${(params.item.eqpPrdnLnQty || 0).toLocaleString()} / ${(
          (params.item.machEqpQty || 0) + (params.item.elpwEqpQty || 0)
        ).toLocaleString()}`,
    },
    {
      header: String(t('ut.label.Electrocity[KW]', 'Electrocity[kW]')),
      collapseTo: 'od2EcNvl',
      columns: [
        {
          binding: 'od2EcNvl',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'elpwStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          // 수용률 = 사용량 / 실사용량 (계산용 필드로 DB에 값저장하지 않음)
          binding: 'elecDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.CDA[l/min]', 'CDA[ℓ/min]')),
      collapseTo: 'cdaCspSumCapa',
      columns: [
        {
          binding: 'cdaCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'cdaStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'cdaDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.N2 Peak[Nl/min]', 'N2 Peak[Nl/min]')),
      collapseTo: 'nitrPeakCspSumCapa',
      columns: [
        {
          binding: 'nitrPeakCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'nitrStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'nitrDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.N2 AVG[Nl/min]', 'N2 AVG[Nl/min]')),
      collapseTo: 'nitrCspSumCapa',
      columns: [
        {
          binding: 'nitrCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'nitrStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'nitrAvgDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.IW[l/min]', 'IW[ℓ/min]')),
      collapseTo: 'iwCspSumCapa',
      columns: [
        {
          binding: 'iwCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'iwStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'iwDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.IW2[l/min]', 'FW[ℓ/min]')),
      collapseTo: 'frwtCspSumCapa',
      columns: [
        {
          binding: 'frwtCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'frwtStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'frwtDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.PCW[l/min]', 'PCW[ℓ/min]')),
      collapseTo: 'coolCspSumCapa',
      columns: [
        {
          binding: 'coolCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'coolStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'coolDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.WW[l/min]', 'WW[ℓ/min]')),
      collapseTo: 'wwCspSumCapa',
      columns: [
        {
          binding: 'wwCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'wwStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'wwDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Steam[Ton/hr]', 'Steam[t/hr]')),
      collapseTo: 'stemCspSumCapa',
      columns: [
        {
          binding: 'stemCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'stemStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'steamDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.NG[N㎥/hr]', 'NG[N㎥/hr]')),
      collapseTo: 'lngCspSumCapa',
      columns: [
        {
          binding: 'lngCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'lngStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'lngDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Return air[CMH]', 'Return air[CMH]')),
      collapseTo: 'insdExhaCspSumCapa',
      columns: [
        {
          binding: 'insdExhaCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'insdExhaStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'insdExhaDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Exhaust air[CMH]', 'Exhaust air[CMH]')),
      collapseTo: 'otsdExhaCspSumCapa',
      columns: [
        {
          binding: 'otsdExhaCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'otsdExhaStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'otsdExhaDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
    {
      header: String(t('ut.label.Vacuum Pump[CMH]', 'Vacuum Pump[CMH]')),
      collapseTo: 'vacmPumpCspSumCapa',
      columns: [
        {
          binding: 'vacmPumpCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'vacmPumptndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'vacmPumpDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Supply Air[CMH]', 'Supply air[CMH]')),
      collapseTo: 'suarCspSumCapa',
      columns: [
        {
          binding: 'suarCspSumCapa',
          header: String(t('ut.label.사용량', '사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'suarStndUseQty',
          header: String(t('ut.label.표준사용량', '표준사용량')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'suarDmdRate',
          header: String(t('ut.label.수용율', '수용율')),
          minWidth: 70,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
  ];

  const handleSubmit = () => {
    props?.onSubmit?.();
  };

  const handleModify = () => {
    props?.onModify?.();
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.표준사용량 비교', '표준사용량 비교')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {condition?.utmNo && condition?.prdnProcCdNm && condition?.eqclNm && (
            <div className="info warning">
              {condition?.utmNo} / {condition?.prdnProcCdNm} / {condition?.eqclNm}
            </div>
          )}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => searchStandardUse(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={props?.isOpenCondition ? defaultGridHeight + 55 : defaultGridHeight + 120 + 38 + 55}
        isReadOnly={true}
        isSelector={false}
        allowPinning={false}
        frozenColumns={3}
        excludeFilter={['prdnProcCdNm', 'qty']}
      />
      <GlobalBtnGroup>
        {isWrite && isUtWriter && (
          <Button
            css={IconButton.button}
            className="save"
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {t('com.button.제출', '제출')}
          </Button>
        )}
        {isWriteComplete && isUtWriter && (
          <Button
            css={IconButton.button}
            className="save"
            disabled={isLoading}
            onClick={handleModify}
          >
            {t('com.button.수정', '수정')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default React.forwardRef(StandardUseContent);
